import type { Query, QueryClient, QueryKey } from "@tanstack/react-query";

import type { RealtimeEnvelope } from "@/shared/realtime/envelope";

/**
 * What a live event refreshes, by the family in its name ("task.moved" is a task event).
 * New events of a known family (task.dependency_added, task.unblocked...) need no change here,
 * and an unknown family refreshes as much as a task event does.
 *
 * Each entry is a query-key root as `makeKeys` builds it. Inside a root, a query refreshes only
 * when it is about this event's project or task (see `concerns`), or about nothing in particular.
 */
const FAMILY_ROOTS: Readonly<Record<string, readonly string[]>> = {
  task: ["tasks", "projects", "plan", "activity", "comments", "files", "approvals", "calendar"],
  milestone: ["plan", "tasks", "projects", "activity", "calendar"],
  phase: ["plan", "tasks", "projects", "activity", "calendar"],
  label: ["plan", "tasks", "activity"],
  project: ["projects", "plan", "tasks", "activity", "calendar", "work-pickers"],
  file: ["files", "activity", "tasks"],
  comment: ["comments", "activity", "tasks"],
  approval: ["approvals", "activity", "tasks", "files", "comments"],
  notification: ["notifications"],
  ticket: ["tickets", "dashboard", "notifications"],
};
const FALLBACK_ROOTS = FAMILY_ROOTS.task ?? [];

/* The signed-in person's own views gather work from every project they see. */
const PERSONAL_ROOTS: readonly string[] = ["my-work", "dashboard"];

/* Detail keys of these roots name the thing itself (a project's key, a task's reference), so
   only the matching one refreshes. Other roots' details refresh whenever their project does. */
const IDENTIFIED_DETAILS: ReadonlySet<string> = new Set(["tasks", "projects", "tickets"]);

/*
 * Events that bump the task's version. When the cache already holds that version (or newer),
 * the event is an echo of what this tab did or already fetched, and is skipped. Events that
 * change something beside the task (its dependencies, comments, labels) never are.
 */
const VERSIONED_TASK_EVENTS: ReadonlySet<string> = new Set([
  "task.moved",
  "task.reordered",
  "task.updated",
  "task.assigned",
  "task.unassigned",
  "task.cancelled",
  "task.restored",
]);

export type RealtimeOutcome = "applied" | "stale" | "deferred";

export function familyOf(name: string): string {
  return name.split(".", 1)[0] ?? name;
}

/**
 * The mutation scope the task edits of a project queue in (features/tasks/api.ts). Tasks
 * outside any project (null) share the queue of boards of many projects.
 */
export function taskEditScope(projectKey: string | null): string {
  return `task-edits:${projectKey ?? "any"}`;
}

/** Whether edits to this project's tasks (or to tasks outside any) are still on their way. */
export function hasPendingEdits(queryClient: QueryClient, projectKey: string | null): boolean {
  const scopeId = taskEditScope(projectKey);
  return (
    queryClient.getMutationCache().findAll({
      status: "pending",
      predicate: (mutation) => mutation.options.scope?.id === scopeId,
    }).length > 0
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Every task-shaped object a cached query holds: a list, pages of items, or one task. */
function cachedVersions(data: unknown, taskId: string): number[] {
  const candidates: unknown[] = Array.isArray(data)
    ? data
    : isRecord(data) && Array.isArray(data.pages)
      ? (data.pages as unknown[]).flatMap((page) =>
          isRecord(page) && Array.isArray(page.items) ? (page.items as unknown[]) : [],
        )
      : [data];
  return candidates.flatMap((item) =>
    isRecord(item) && item.id === taskId && typeof item.version === "number" ? [item.version] : [],
  );
}

/** The newest version of a task anywhere in the task cache, if it is cached at all. */
export function newestCachedVersion(queryClient: QueryClient, taskId: string): number | undefined {
  let newest: number | undefined;
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ["tasks"] })) {
    for (const version of cachedVersions(query.state.data, taskId)) {
      if (newest === undefined || version > newest) newest = version;
    }
  }
  return newest;
}

function same(left: unknown, right: string | null): boolean {
  return right !== null && typeof left === "string" && left.toUpperCase() === right.toUpperCase();
}

function namesThis(value: unknown, envelope: RealtimeEnvelope): boolean {
  return [envelope.project_key, envelope.reference, envelope.task_id, envelope.project_id].some(
    (id) => same(value, id),
  );
}

/** Whether a cached query is about this event's project or task (or about nothing specific). */
export function concerns(queryKey: QueryKey, envelope: RealtimeEnvelope): boolean {
  const [root, kind, identity] = queryKey;
  if (kind === "detail") {
    return typeof root === "string" && IDENTIFIED_DETAILS.has(root)
      ? namesThis(identity, envelope)
      : true;
  }
  const filters = queryKey.find(isRecord);
  if (filters === undefined) return true;
  if ("project" in filters) {
    // A list of tasks outside any project (subtasks of T-12) holds a null project.
    if (filters.project === null) return envelope.project_key === null;
    return same(filters.project, envelope.project_key);
  }
  if ("task" in filters) return same(filters.task, envelope.task_id);
  return true;
}

function isStaleEcho(queryClient: QueryClient, envelope: RealtimeEnvelope): boolean {
  if (!VERSIONED_TASK_EVENTS.has(envelope.name)) return false;
  if (envelope.task_id === null || envelope.version === null) return false;
  const cached = newestCachedVersion(queryClient, envelope.task_id);
  return cached !== undefined && cached >= envelope.version;
}

/**
 * Bring the cache up to date with one live event: refetch what it touched, nothing else.
 *
 * - "stale": the cache already holds this version of the task (this tab made the change).
 * - "deferred": edits to the project's tasks are still in flight, so refetching now could
 *   overwrite them on screen; apply the event again once they settle.
 * - "applied": the affected queries were invalidated (active ones refetch at once).
 */
export function applyRealtimeEvent(
  queryClient: QueryClient,
  envelope: RealtimeEnvelope,
): RealtimeOutcome {
  const family = familyOf(envelope.name);
  const projectKey = envelope.project_key;
  if (family === "task" && hasPendingEdits(queryClient, projectKey)) {
    return "deferred";
  }
  if (isStaleEcho(queryClient, envelope)) return "stale";
  const roots = new Set(FAMILY_ROOTS[family] ?? FALLBACK_ROOTS);
  void queryClient.invalidateQueries({
    predicate: (query: Query) => {
      const root = query.queryKey[0];
      if (typeof root !== "string") return false;
      if (roots.has(root)) return concerns(query.queryKey, envelope);
      return family !== "notification" && PERSONAL_ROOTS.includes(root);
    },
  });
  return "applied";
}
